import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { memo, useCallback, useEffect, useState } from 'react';
import { MapMouseEvent, Map as MapboxMap, ViewStateChangeEvent, useMap } from 'react-map-gl/mapbox';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { environment } from '../../environments/environment';
import { useDebounceFunction } from '../../hooks/useDebounce';
import { getPublicMapCurrentStates } from '../../services/mapService';
import { showClustersState } from '../../storage/app';
import {
  bottomMapPaddingState,
  interactiveLayersState,
  leftMapPaddingState,
  mapCurrentStatesState,
  mapStyleState,
  mapViewState,
  navCtrl,
  rightMapPaddingState,
  topMapPaddingState,
} from '../../storage/map';
import { MapPointerInfo } from '../../types/app';
import { MapOptions } from './MapOptions';
import { MapSearchBox } from './MapSearchBox';
import { PointerInfo } from './PointerInfo';
import SpotsLayer from './layers/SpotsLayer';
import { TrafficLayer } from './layers/TrafficLayer';
import { ZoneFencesLayer } from './layers/ZoneFencesLayer';
import Clusters from './markers/Clusters';
import TempPositionMarker from './markers/TempPositionMarker';
import './styles/mapbox.css';

function PublicMap() {
  const { map } = useMap();
  const [viewState, setViewState] = useRecoilState(mapViewState);
  const [pointerInfo, setPointerInfo] = useState<MapPointerInfo>();
  const mapStyle = useRecoilValue(mapStyleState);
  const interactiveLayers = useRecoilValue(interactiveLayersState);
  const leftPadding = useRecoilValue(leftMapPaddingState);
  const rightPadding = useRecoilValue(rightMapPaddingState);
  const bottomPadding = useRecoilValue(bottomMapPaddingState);
  const topPadding = useRecoilValue(topMapPaddingState);
  const setMapCurrentStates = useSetRecoilState(mapCurrentStatesState);
  const showClusters = useRecoilValue(showClustersState);
  const [activeNavCtrl, setActiveNavCtrl] = useRecoilState(navCtrl);
  const { shareLinkId } = useParams();

  const mapResize = useDebounceFunction(function () {
    map?.resize();
  }, 0);

  useEffect(() => {
    if (map) {
      queueMicrotask(() => {
        mapResize();
      });
    }
  }, [map]);

  useEffect(() => {
    queueMicrotask(() => {
      map?.resize();
    });
    if (map && !activeNavCtrl) {
      const nav = new mapboxgl.NavigationControl({ showCompass: false });
      map?.addControl(nav, 'bottom-right');
      setActiveNavCtrl(!activeNavCtrl);
    }
  }, [map]);

  useEffect(() => {
    if (!map || !shareLinkId) return;
    updateMapCurrentStates();
  }, [map, leftPadding, rightPadding, bottomPadding, topPadding, shareLinkId]);

  const updateMapCurrentStates = async () => {
    if (!map || !shareLinkId) return;
    const bounds = map.getBounds();
    const northWest = bounds?.getNorthWest();
    const southEast = bounds?.getSouthEast();
    if (northWest && southEast) {
      const { currentStates, error } = await getPublicMapCurrentStates({
        shareLinkId,
        x0: northWest.lng,
        y0: northWest.lat,
        x1: southEast.lng,
        y1: southEast.lat,
      });
      if (!error && currentStates) {
        setMapCurrentStates(currentStates);
      } else {
        setMapCurrentStates(undefined);
      }
    }
  };

  const onMoveEnd = (ev: ViewStateChangeEvent) => {
    setViewState(ev.viewState);
    updateMapCurrentStates();
  };

  const onMouseMove = useCallback((event: MapMouseEvent) => {
    const {
      features,
      point: { x, y },
    } = event;
    let properties = {};
    if (features?.length) {
      const allProps = features.map((feature) => feature.properties);
      properties = Object.assign({}, ...allProps);
    }
    setPointerInfo(features && features[0] && { properties, x, y });
  }, []);

  const onClick = async (ev: MapMouseEvent) => {
    // const point = {
    //   lat: ev.lngLat.lat,
    //   lng: ev.lngLat.lng,
    // };
  };

  return (
    <MapboxMap
      id="map"
      reuseMaps
      mapboxAccessToken={environment.mapboxAPIKey}
      initialViewState={{ ...viewState }}
      onClick={onClick}
      mapStyle={mapStyle}
      onMoveEnd={onMoveEnd}
      onMouseMove={onMouseMove}
      interactiveLayerIds={interactiveLayers}
      projection={{ name: 'mercator' }}
      padding={{
        left: leftPadding,
        bottom: bottomPadding,
        right: rightPadding,
        top: topPadding,
      }}
      style={{ position: 'absolute' }}
      scrollZoom={true}
      keyboard={true}
      doubleClickZoom={true}
      dragRotate={false}
      minZoom={0}
      maxZoom={18}
    >
      <PointerInfo info={pointerInfo} />
      <SpotsLayer />
      <ZoneFencesLayer />
      <TrafficLayer />
      {showClusters && <Clusters />}
      <MapSearchBox />
      <MapOptions />
      <TempPositionMarker />
    </MapboxMap>
  );
}

export default memo(PublicMap);
